/**
 * The draft a NEW editor starts from. A container opened from a campaign
 * starts on that campaign's master config (sections seeded from it, the
 * campaign from it); a master config opened from a campaign starts on that
 * campaign — blank, or as a COPY of another campaign's master config (every
 * section copied, nothing saved until Save). This is also the dirty
 * baseline, so closing an untouched new editor never asks to discard.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenEffectiveConfig, DropGenMasterRef } from '@trafficpulse/shared';

import type { ContainerDraft, DropGenContainerDetail } from '../../../../drop-generator.types';
import { apiToDraft, emptyDraft } from '../container-draft';
import { applyMaster } from './own-sections';
import { autoSendSeed } from '../../autosend/autosend-slice';

export interface EditorPreset {
  /** New container: the master config it follows from the start. */
  masterId?: string;
  /** New master config: its group's representative campaign. New container:
   *  its campaign, when the group has only one. */
  campaignWiserId?: number;
  /** New master config: a suggested name. */
  name?: string;
  /** New master config: the master config whose sections it copies. */
  copyFromMasterId?: string;
  /** New route-test container. */
  kind?: 'ROUTE_TEST';
}

export interface PresetSources {
  master?: {
    ref: DropGenMasterRef;
    config: DropGenEffectiveConfig | null;
  } | null;
  /** The loaded master config to copy (new master config only). */
  copy?: DropGenContainerDetail | null;
}

export function newDraft(mode: 'container' | 'master', preset: EditorPreset | undefined, sources: PresetSources): ContainerDraft {
  if (mode === 'container') {
    const base = emptyDraft();
    const seeded = sources.master ? applyMaster(base, sources.master.ref, sources.master.config, preset?.campaignWiserId ?? null) : base;
    return preset?.kind === 'ROUTE_TEST'
      ? {
          ...seeded,
          kind: 'ROUTE_TEST',
          name: seeded.name || 'Route test',
          ownSections: {
            ...seeded.ownSections,
            gatewayStrategy: true,
            gatewayPool: true,
          },
          gateway: {
            ...seeded.gateway,
            strategy: 'ALTERNATE',
            orderMode: 'MANUAL',
          },
        }
      : seeded;
  }
  const base = sources.copy
    ? {
        ...apiToDraft(sources.copy),
        slots: [],
        groups: [],
        staticTagId: null,
        enabled: true,
      }
    : emptyDraft();
  return {
    ...base,
    kind: 'MASTER',
    masterId: null,
    ownSections: {},
    campaignWiserId: preset?.campaignWiserId ?? null,
    name: preset?.name ?? '',
    // A copy carries the other group's cap and stop-loss, never its switch.
    ...(sources.copy
      ? {
          autoSend: { ...base.autoSend, enabled: false },
          autoSendBase: autoSendSeed(null).autoSendBase,
        }
      : {}),
  };
}
